import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { listUsers } from "../../redux-components/actions/userActions";
import {
  listConsoleItems,
  createConsoleItem,
  updateConsoleItem,
  deleteConsoleItem,
  addListItem,
  updateListItem,
  deleteListItem
} from '../../redux-components/actions/consoleActions';
import axios from 'axios';
import { customSwal } from "../../components/CustomSwal";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import AdminCreateConsoleModal from '../../components/Console/AdminCreateConsoleModal';
import CreateItemModal from '../../components/Console/CreateItemModal';
import EditItemModal from '../../components/Console/EditItemModal';
import ConsoleItemsBrowser from '../../components/Console/ConsoleItemsBrowser';
import ConsoleMediaLibrary from '../../components/Console/ConsoleMediaLibrary';
import AdminImageCleanup from '../../components/Console/AdminImageCleanup';
import ListItemModal from '../../components/Console/ListItemModal';
import CustomModal from '../../components/Modal/CustomModal';

const AdminCreateConsole = () => {
    const history = useNavigate();
    const dispatch = useDispatch();
    const [selectedUser, setSelectedUser] = useState(null);
    const [consoles, setConsoles] = useState([]);
    const [loadingConsoles, setLoadingConsoles] = useState(false);
    const [activeConsole, setActiveConsole] = useState(null);

    // Modal states
    const [createConsoleModalOpen, setCreateConsoleModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [listItemModalOpen, setListItemModalOpen] = useState(false);

    // Edit/Delete states
    const [editItem, setEditItem] = useState(null);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [currentConsoleItemId, setCurrentConsoleItemId] = useState(null);
    const [editingListItem, setEditingListItem] = useState(null);
    const [mediaOpen, setMediaOpen] = useState(false);

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;
    const userList = useSelector((state) => state.userList);
    const { loading: usersLoading, error: usersError, users } = userList;

    // Use the same Redux selectors as the user interface
    const consoleItemCreate = useSelector((state) => state.consoleItemCreate);
    const { success: successCreateItem } = consoleItemCreate;

    const consoleItemUpdate = useSelector((state) => state.consoleItemUpdate);
    const { success: successUpdateItem } = consoleItemUpdate;

    const consoleItemDelete = useSelector((state) => state.consoleItemDelete);
    const { success: successDeleteItem } = consoleItemDelete;

    const listItemAdd = useSelector((state) => state.listItemAdd);
    const { success: successAddListItem } = listItemAdd;

    const listItemUpdate = useSelector((state) => state.listItemUpdate);
    const { success: successUpdateListItem } = listItemUpdate;

    const listItemDelete = useSelector((state) => state.listItemDelete);
    const { success: successDeleteListItem } = listItemDelete;

    useEffect(() => {
        async function getUsersList() {
            if(userInfo && userInfo.isAdmin) {
                await dispatch(listUsers());
            }
        }
        getUsersList();
    }, [dispatch, userInfo]);

    useEffect(() => {
        if (users && users.length > 0 && !selectedUser) {
            setSelectedUser(users[0]);
            fetchUserConsoles(users[0]._id);
        }
    }, [users, selectedUser]);

    useEffect(() => {
        if (activeConsole) {
            dispatch(listConsoleItems(activeConsole));
        }
    }, [dispatch, activeConsole]);

    // Handle success states just like the user interface
    useEffect(() => {
        if (successCreateItem || successUpdateItem || successDeleteItem ||
            successAddListItem || successUpdateListItem || successDeleteListItem) {
            if (activeConsole) {
                dispatch(listConsoleItems(activeConsole));
            }
            closeAllModals();
        }
    }, [dispatch, successCreateItem, successUpdateItem, successDeleteItem,
        successAddListItem, successUpdateListItem, successDeleteListItem, activeConsole]);

    if(!userInfo || !userInfo?.isAdmin) {
        return null;
    }

    const handleUserChange = (e) => {
        const selectedUsername = e.target.value;
        const user = users.find(user => user.username === selectedUsername);
        setSelectedUser(user);
        setActiveConsole(null);
        fetchUserConsoles(user._id);
    };

    const fetchUserConsoles = async (userId) => {
        try {
            setLoadingConsoles(true);
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`,
                },
            };

            const { data } = await axios.get(
                `/api/consoles/user/${userId}`,
                config
            );

            setConsoles(data);
            if (data.length > 0) {
                setActiveConsole(data[0]._id);
            }
            setLoadingConsoles(false);
        } catch (error) {
            setLoadingConsoles(false);
            customSwal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response && error.response.data.message
                    ? error.response.data.message
                    : error.message,
            });
        }
    };

    const handleConsoleChange = (e) => {
        setActiveConsole(e.target.value);
    };

    const closeAllModals = () => {
        setCreateConsoleModalOpen(false);
        setCreateModalOpen(false);
        setEditModalOpen(false);
        setDeleteModalOpen(false);
        setListItemModalOpen(false);
        setEditItem(null);
        setDeleteItemId(null);
        setCurrentConsoleItemId(null);
        setEditingListItem(null);
    };

    const openCreateModal = () => {
        setCreateModalOpen(true);
    };

    const openEditModal = (item) => {
        setEditItem(item);
        setEditModalOpen(true);
    };

    const openDeleteModal = (itemId) => {
        setDeleteItemId(itemId);
        setDeleteModalOpen(true);
    };

    const handleDeleteItem = () => {
        dispatch(deleteConsoleItem(deleteItemId));
    };

    const openListItemModal = (consoleItemId, listItem = null) => {
        setCurrentConsoleItemId(consoleItemId);
        setEditingListItem(listItem);
        setListItemModalOpen(true);
    };

    const handleDeleteListItem = (consoleItemId, listItemId) => {
        customSwal.fire({
            title: 'Are you sure?',
            text: 'This will delete the list item permanently.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteListItem(consoleItemId, listItemId));
            }
        });
    };

    const refreshUserConsoles = () => {
        if (selectedUser) {
            fetchUserConsoles(selectedUser._id);
        }
    };

    return (
        <div className="p-4 w-full">
            <h2 className="text-xl font-bold mb-4">Admin Console Management</h2>

            <AdminImageCleanup />

            <div className="mb-4">
                <label className="block mb-2 font-medium">Select User:</label>
                <select
                    className="text-black p-2 rounded w-full max-w-md"
                    onChange={handleUserChange}
                    value={selectedUser?.username || ''}
                >
                    {users?.map((user) => (
                        <option key={user._id} value={user.username} className="text-black">
                            {user.username}
                        </option>
                    ))}
                </select>
            </div>

            {selectedUser && (
                <div className="bg-gray-800 p-4 rounded-md max-w-md mb-4 mx-auto select-all">
                    <h3 className="text-lg font-bold mb-2">Selected User Information</h3>
                    <div className="space-y-2">
                        <p><span className="font-medium select-text">User ID:</span> {selectedUser._id}</p>
                        <p><span className="font-medium">Username:</span> {selectedUser.username}</p>
                        <p><span className="font-medium">Email:</span> {selectedUser.email}</p>
                        <p><span className="font-medium">Role:</span> {selectedUser.isAdmin ? 'Admin' : 'User'}</p>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">User Consoles</h3>
                <button
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center"
                    onClick={() => setCreateConsoleModalOpen(true)}
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Create Console
                </button>
            </div>

            {loadingConsoles ? (
                <Loader />
            ) : consoles.length === 0 ? (
                <Message>No consoles found for this user. Create one to get started.</Message>
            ) : (
                <>
                    <div className="mb-4">
                        <label className="block mb-2 font-medium">Select Console:</label>
                        <select
                            className="text-black p-2 rounded w-full max-w-md"
                            onChange={handleConsoleChange}
                            value={activeConsole || ''}
                        >
                            {consoles.map((console) => (
                                <option key={console._id} value={console._id} className="text-black">
                                    {console.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    {activeConsole && (
                        <div className="mt-6 w-full">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold">Console Items</h3>
                                <div className="flex items-center gap-2">
                                    <button
                                        className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded flex items-center"
                                        onClick={() => setMediaOpen(true)}
                                    >
                                        <FontAwesomeIcon icon={faImages} className="mr-2" />
                                        Media
                                    </button>
                                    <button
                                        className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center"
                                        onClick={openCreateModal}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                        Add Item
                                    </button>
                                </div>
                            </div>

                            <ConsoleItemsBrowser
                                activeConsole={activeConsole}
                                onCreate={openCreateModal}
                                onEdit={openEditModal}
                                onDelete={openDeleteModal}
                                onAddListItem={openListItemModal}
                                onEditListItem={openListItemModal}
                                onDeleteListItem={handleDeleteListItem}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Modals - Using the exact same components as the user interface */}
            <AdminCreateConsoleModal
                isOpen={createConsoleModalOpen}
                onClose={() => setCreateConsoleModalOpen(false)}
                selectedUser={selectedUser}
                userInfo={userInfo}
                onSuccess={refreshUserConsoles}
            />

            <CreateItemModal
                isOpen={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                activeConsole={activeConsole}
                dispatch={dispatch}
                createConsoleItem={createConsoleItem}
            />

            <EditItemModal
                isOpen={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                item={editItem}
                dispatch={dispatch}
                updateConsoleItem={updateConsoleItem}
            />

            <ListItemModal
                isOpen={listItemModalOpen}
                onClose={() => setListItemModalOpen(false)}
                consoleItemId={currentConsoleItemId}
                listItem={editingListItem}
                dispatch={dispatch}
                addListItem={addListItem}
                updateListItem={updateListItem}
            />

            <ConsoleMediaLibrary
                isOpen={mediaOpen}
                onClose={() => setMediaOpen(false)}
                consoleId={activeConsole}
            />

            {/* Delete Confirmation Modal */}
            <CustomModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Delete Console Item"
                onConfirm={handleDeleteItem}
                confirmText="Delete"
                size="sm"
            >
                <p>Are you sure you want to delete this item? This action cannot be undone.</p>
            </CustomModal>
        </div>
    );
};

export default AdminCreateConsole;